/**
 * lib/suppliers.ts
 * ------------------------------------------------------------------
 * WHO THE SCHOOL PAYS, AND INTO WHICH ACCOUNT.
 *
 * PURE. A supplier record in, an answer out. No Firestore, no React.
 *
 * BANK DETAILS ARE THE MOST DANGEROUS FIELD IN A FINANCE SYSTEM. Supplier
 * fraud almost never involves breaking into anything: somebody emails the
 * finance office on convincing letterhead saying "our banking has changed",
 * the office updates the record, and the next genuine invoice is paid to a
 * stranger. The school loses real money and every control it has — approval
 * limits, segregation of duties, three quotes — passes, because the payment
 * itself was entirely proper.
 *
 * So this module treats banking as its own thing, with three rules that
 * exist for that one scenario:
 *
 *   CHANGING BANK DETAILS UN-VERIFIES THEM. Always, automatically, and not
 *   as a courtesy the person changing them can decline. Verification means
 *   "somebody rang the number on the original contract and confirmed it",
 *   and that statement expires the moment the digits change.
 *
 *   EVERY CHANGE IS KEPT. Not "the record was updated" — the old account
 *   number, the new one, who changed it and when. After a fraud, the
 *   question is always "when did this change and who did it", and a record
 *   that only holds the current value cannot answer it.
 *
 *   TWO SUPPLIERS ON ONE ACCOUNT IS REPORTED. Occasionally legitimate — a
 *   parent company, a sole trader with two names. Usually it is one supplier
 *   entered twice, and sometimes it is an employee's account attached to a
 *   supplier nobody has met.
 *
 * DE-DUPED ON A NORMALISED NAME, because the same supplier arrives as
 * "Gaborone Stationers", "gaborone stationers " and "GABORONE STATIONERS
 * (PTY) LTD" and paying all three separately is how a school ends up with
 * three ledgers for one relationship.
 * ------------------------------------------------------------------ */

export const SUPPLIER_COLLECTION = 'financeSuppliers';

export interface SupplierBank {
  /** The name ON the account, which is not always the supplier's trading name. */
  accountName?: string;
  accountNumber?: string;
  bankName?: string;
  branch?: string;
}

export interface SupplierBankChange {
  at: string;
  by?: string;
  byName?: string;
  /** What it was. Absent on the first set of details. */
  from?: SupplierBank;
  to: SupplierBank;
  /** Why, in the words of whoever changed it. */
  reason?: string;
}

export interface Supplier {
  id: string;
  name: string;
  /** Normalised, for de-duplication and matching. */
  nameKey: string;
  contactPerson?: string;
  contactPhone?: string;
  /** Where remittance advice goes. */
  contactEmail?: string;
  address?: string;
  /** BURS taxpayer identification number. */
  tin?: string;
  vatNumber?: string;
  bank?: SupplierBank;
  /**
   * When somebody confirmed these details against something other than the
   * email that asked for the change. CLEARED whenever the details change.
   */
  bankVerifiedAt?: string;
  bankVerifiedBy?: string;
  bankVerifiedByName?: string;
  /** How it was confirmed — "rang the number on the contract", "cancelled cheque". */
  bankVerifiedHow?: string;
  /** Every change, oldest first. Never pruned. */
  bankHistory?: SupplierBankChange[];
  active: boolean;
  notes?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/* ----------------------------- identity ----------------------------- */

/**
 * The match key for a supplier name.
 *
 * Case, spacing and the company-form suffix are all noise: a school does not
 * have two relationships with "Gaborone Stationers" and "GABORONE STATIONERS
 * (PTY) LTD", it has one, and paying them as two is how the ledger ends up
 * with a supplier balance nobody can reconcile.
 */
export function supplierKey(name: string): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\((pty|proprietary)\)/g, '')
    .replace(/\b(pty|proprietary|ltd|limited|inc|cc|t\/a)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A deterministic document id, so the same name always upserts.
 *
 * The fallback guards the KEY, not the concatenation. `'sup_' + x || 'sup_unknown'`
 * parses as `('sup_' + x) || …`, which is always truthy, so the fallback is
 * dead code and an empty name yields the id `sup_` — one document every
 * unnamed supplier then shares.
 */
export function supplierDocId(name: string): string {
  const slug = supplierKey(name).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return `sup_${slug || 'unknown'}`;
}

/* ---------------------------- validation ---------------------------- */

export function supplierProblem(s: Pick<Supplier, 'name' | 'contactEmail'>): string | null {
  const name = String(s.name ?? '').trim();
  if (name.length < 2) return 'Give the supplier a name.';
  if (name.length > 120) return 'That name is too long.';
  if (s.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.contactEmail.trim())) {
    return 'That email address does not look right.';
  }
  return null;
}

/**
 * Why these bank details cannot be saved, or null if they can.
 *
 * An account number is checked for shape rather than validated against a
 * bank: Botswana account numbers vary by institution and a rule tight enough
 * to be useful would reject somebody's real account. Length and digits catch
 * the transcription slip, which is the realistic error.
 */
export function bankProblem(b: SupplierBank): string | null {
  const num = String(b.accountNumber ?? '').replace(/[\s-]/g, '');
  if (!num) return 'Enter the account number.';
  if (!/^\d{6,20}$/.test(num)) return 'An account number is 6 to 20 digits.';
  if (!String(b.bankName ?? '').trim()) return 'Name the bank.';
  if (!String(b.accountName ?? '').trim()) {
    return 'Enter the name on the account — it is what a bank checks, and it is not '
      + 'always the supplier’s trading name.';
  }
  return null;
}

/** Digits only, so two records of the same account compare equal. */
export function normaliseAccountNumber(n?: string): string {
  return String(n ?? '').replace(/[^\d]/g, '');
}

/** Have the bank details actually changed? Formatting alone has not. */
export function bankDetailsDiffer(a?: SupplierBank, b?: SupplierBank): boolean {
  if (!a && !b) return false;
  if (!a || !b) return true;
  return normaliseAccountNumber(a.accountNumber) !== normaliseAccountNumber(b.accountNumber)
    || String(a.bankName ?? '').trim().toLowerCase() !== String(b.bankName ?? '').trim().toLowerCase()
    || String(a.accountName ?? '').trim().toLowerCase() !== String(b.accountName ?? '').trim().toLowerCase();
}

/**
 * Apply a change of bank details.
 *
 * PURE, and it is the single place the un-verify rule lives. A caller
 * cannot forget it, and a caller cannot decline it: the point of
 * verification is that somebody confirmed THESE digits, and that statement
 * does not survive the digits changing.
 */
export function applyBankChange(
  supplier: Supplier,
  next: SupplierBank,
  by: { uid?: string; name?: string },
  reason?: string,
  at: string = new Date().toISOString(),
): Supplier {
  if (!bankDetailsDiffer(supplier.bank, next)) {
    // Nothing meaningful changed — re-typing the same number with different
    // spacing must not clear a verification somebody did properly.
    return { ...supplier, bank: next, updatedAt: at, updatedBy: by.uid };
  }
  const entry: SupplierBankChange = {
    at,
    ...(by.uid ? { by: by.uid } : {}),
    ...(by.name ? { byName: by.name } : {}),
    ...(supplier.bank ? { from: supplier.bank } : {}),
    to: next,
    ...(reason ? { reason } : {}),
  };
  const out: Supplier = {
    ...supplier,
    bank: next,
    bankHistory: [...(supplier.bankHistory ?? []), entry],
    updatedAt: at,
    updatedBy: by.uid,
  };
  delete out.bankVerifiedAt;
  delete out.bankVerifiedBy;
  delete out.bankVerifiedByName;
  delete out.bankVerifiedHow;
  return out;
}

/** Are this supplier's banking details currently confirmed? */
export function isBankVerified(s: Supplier): boolean {
  return Boolean(s.bank?.accountNumber && s.bankVerifiedAt);
}

/** When the banking last changed, or null if it never has. */
export function bankLastChangedAt(s: Supplier): string | null {
  const h = s.bankHistory ?? [];
  return h.length ? h[h.length - 1].at : null;
}

/* -------------------------- shared accounts -------------------------- */

export interface SharedAccount {
  accountNumber: string;
  suppliers: { id: string; name: string }[];
}

/**
 * Suppliers sharing one bank account.
 *
 * Occasionally legitimate — a parent company, a sole trader operating under
 * two names. Usually it is one supplier entered twice, which means two
 * ledgers for one relationship and a balance nobody can reconcile. Sometimes
 * it is an employee's own account attached to a supplier nobody has met.
 */
export function sharedBankAccounts(suppliers: Supplier[]): SharedAccount[] {
  const byAccount = new Map<string, { id: string; name: string }[]>();
  for (const s of suppliers) {
    const num = normaliseAccountNumber(s.bank?.accountNumber);
    if (!num) continue;
    byAccount.set(num, [...(byAccount.get(num) ?? []), { id: s.id, name: s.name }]);
  }
  return [...byAccount.entries()]
    .filter(([, list]) => list.length > 1)
    .map(([accountNumber, list]) => ({ accountNumber, suppliers: list }));
}

/**
 * Suppliers whose names are near-identical after normalising.
 *
 * Catches what the de-duplicating id does not: two records created before
 * this register existed, or names that differ by a typo rather than by a
 * company-form suffix.
 */
export function probableDuplicates(suppliers: Supplier[]): Supplier[][] {
  const byKey = new Map<string, Supplier[]>();
  for (const s of suppliers) {
    const k = s.nameKey || supplierKey(s.name);
    byKey.set(k, [...(byKey.get(k) ?? []), s]);
  }
  return [...byKey.values()].filter((g) => g.length > 1);
}
